import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityFlagsActionType,
  IActivityFiltersAction,
  IActivityFiltersState,
  IActivityFlagsAction,
} from 'containers/CRM/hooks/useActivityFilters';
import { CheckboxList, ICheckboxOption } from 'ui/CheckboxList/CheckboxList';
import { ECrmActivityType } from 'services/CrmApi/types/CrmActivityTypes';
import { format } from 'date-fns';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { StyledDateRangeInput } from 'containers/BookingList/StyledFilters';
import { DatePickerWrapper } from 'pureUi/providers/DatePickerStateProvider/DatePickerWrapper';
import SingleSelect, { ISingleSelectOption } from 'ui/SingleSelect';
import classNames from 'classnames';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { IUseCrmDirectoryEntriesParams, useCrmDirectoryEntries } from 'services/CrmApi/queries/useCrmDirectoryEntries';
import { ESortOrder } from 'store/common/types';
import { ECrmDirectoryListSortableKey } from 'services/CrmApi/types/DirectoryEntry';
import TextInput from 'pureUi/TextInput';
import MagnifyingIcon from 'ui/Icons/magnifying-glass.component.svg';
import FluidButton from 'ui/FluidButton';
import { SvgIcon } from 'ui/SvgIcon';
import classnames from 'classnames';
import { ArrowDownComponent } from 'ui/Icons';
import AnimateHeight from 'react-animate-height';
import { removeActivityIdFromUrl } from 'containers/CRM/helpers';
import { useBackendUsers } from 'services/BackendApi/queries/useBackendUsers';
import { userToOption } from 'ui/SingleSelect/helpers';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

interface IActivityFiltersProps {
  dispatchActivityFilters: React.Dispatch<IActivityFlagsAction | IActivityFiltersAction>;
  activityFilters: IActivityFiltersState;
  resetHighlightedActivity: () => void;
  disabled?: boolean;
}

export const ActivityFilters: React.FC<IActivityFiltersProps> = ({
  dispatchActivityFilters,
  activityFilters,
  resetHighlightedActivity,
  disabled = false,
}) => {
  const { dynamicParameters } = useDynamicParameters();
  const { isMobile } = useResponsive();
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [createdById, setCreatedById] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [isExpanded, setIsExpanded] = useState(false);

  const crmUsers = useBackendUsers('');
  const crmUsersOptions: ISingleSelectOption[] = crmUsers.data?.data?.data.map(user => userToOption(user)) ?? [];

  const dateRange = useMemo(() => {
    return {
      startDate,
      endDate,
    };
  }, [startDate, endDate]);

  const handleCheckboxFilterChange = useCallback(
    (optionName: ActivityFlagsActionType, optionValue: boolean) => {
      dispatchActivityFilters({ type: optionName, value: optionValue });
    },
    [dispatchActivityFilters]
  );

  const handleResetFilters = useCallback(() => {
    setStartDate(undefined);
    setEndDate(undefined);
    setCreatedById(undefined);
    setSearchTerm('');
    // whenever data changes in useReducer via dispatchActivityFilters, the new http request is executed
    dispatchActivityFilters({ type: 'set_start_date', value: undefined });
    dispatchActivityFilters({ type: 'set_end_date', value: undefined });
    dispatchActivityFilters({ type: 'set_created_by_id', value: undefined });
    dispatchActivityFilters({ type: 'set_search_term', value: undefined });
    dispatchActivityFilters({ type: 'set_search_term', value: undefined });

    resetHighlightedActivity();
  }, [dispatchActivityFilters, resetHighlightedActivity]);

  const handleSearch = useCallback(() => {
    dispatchActivityFilters({ type: 'set_start_date', value: startDate });
    dispatchActivityFilters({ type: 'set_end_date', value: endDate });
    dispatchActivityFilters({ type: 'set_created_by_id', value: createdById });
    dispatchActivityFilters({ type: 'set_search_term', value: searchTerm });

    resetHighlightedActivity();
  }, [dispatchActivityFilters, startDate, endDate, createdById, searchTerm]);

  const toggleFiltersVisibility = useCallback(() => {
    if (isExpanded) {
      // reset search results when hiding filters
      handleResetFilters();
    }
    setIsExpanded(!isExpanded);
  }, [isExpanded, handleResetFilters]);

  const travelAgentOptions: ICheckboxOption[] = [
    {
      label: 'Calls',
      name: 'set_calls_filter',
      value: activityFilters.flags[ECrmActivityType.CALL],
    },
    {
      label: 'Meetings',
      name: 'set_meetings_filter',
      value: activityFilters.flags[ECrmActivityType.MEETING],
    },
    {
      label: 'Notes',
      name: 'set_notes_filter',
      value: activityFilters.flags[ECrmActivityType.NOTE],
    },
    {
      label: 'Tasks',
      name: 'set_tasks_filter',
      value: activityFilters.flags[ECrmActivityType.TASK],
      disabled: !dynamicParameters.ENABLE_CRM_TMS,
    },
  ];

  return (
    <div className="activity-filters flex flex-col gap-[15px] flex-1">
      <div className="checkbox-container">
        <CheckboxList options={travelAgentOptions} onClick={handleCheckboxFilterChange} disabled={disabled} />
      </div>
      <div>
        <div className="flex justify-start items-center cursor-pointer mb-[15px] gap-[5px]" onClick={toggleFiltersVisibility}>
          <p className="font-pt-sans text-[15px] leading-[20px] m-0 text-brown-prime underline">
            {isExpanded ? 'Hide Filters' : 'See Filters'}
          </p>
          <SvgIcon
            IconComponent={ArrowDownComponent}
            className={classnames('transition w-[14px] fill-brown-prime stroke-1 relative', {
              'top-0': isExpanded,
              'rotate-180 top-[3px]': !isExpanded,
            })}
          />
        </div>
        <AnimateHeight duration={300} height={isExpanded ? 'auto' : 0} className={classnames('accordion-area', {})}>
          <div className="other-filters flex flex-col gap-[15px]">
            <p className="font-pt-sans text-17px font-[700] m-0">Filter and Search Options</p>
            <div className="flex flex-col md:flex-row gap-[15px] md:gap-[20px]">
              <div className="date-range-and-created-by flex gap-[20px]">
                <div className="date-range-container flex flex-col gap-[5px]">
                  <label className="font-pt-sans text-black text-13px leading-17px tracking-2xs">Date Range</label>
                  <DatePickerWrapper
                    dateRange={dateRange}
                    calendarCount={isMobile ? 1 : 2}
                    onDateChange={(startDate, endDate) => {
                      setStartDate(startDate);
                      setEndDate(endDate);
                    }}
                  />
                </div>
                <div className="created-by-container flex flex-col gap-[5px] flex-1 md:min-w-[250px]">
                  <SingleSelect
                    fieldId="createdBy"
                    label="Created By"
                    className={classNames('created-by')}
                    value={createdById}
                    maxVisibleItems={isMobile ? 4 : 6}
                    showSelectedOption
                    options={crmUsersOptions ?? []}
                    labelWhenNothingSelected="None"
                    onChange={value => {
                      setCreatedById(value);
                    }}
                  />
                </div>
              </div>
              <div className="search-container flex flex-col gap-[5px] md:flex-1">
                  <label className="text-black font-pt-sans text-13px leading-17px tracking-2xs">Search Content</label>
                  <TextInput
                    id="crm-filter-profile"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchTerm(e.target.value);
                    }}
                    placeholder="Search from directory ..."
                    className="search-dropdown min-w-[180px] max-w-[180px]] max-h-[35px]"
                    value={searchTerm}
                    inputClassName="!py-[10px] !px-[30px] !text-black !bg-white-hard"
                  >
                    <MagnifyingIcon className="absolute w-[18px] top-[10px] left-[10px] fill-light-gray rotate-90" />
                  </TextInput>
              </div>
            </div>
            <div className="flex flex-row">
                <FluidButton
                  className="global-statement-reset-filters-button block"
                  onClick={handleResetFilters}
                  type="secondary"
                >
                  Reset Filters
                </FluidButton>

                <FluidButton
                  className="global-statement-search-button block ml-10px"
                  onClick={handleSearch}
                  type="primary"
                >
                  Search
                </FluidButton>
              </div>
          </div>
        </AnimateHeight>
      </div>
    </div>
  );
};
